import type { TabId } from './useTabParam';

const DEFAULT_LABELS: Record<TabId, string> = {
  home: 'HOME', pass: 'PASS', loot: 'LOOT', more: 'MORE',
};
const ORDER: TabId[] = ['home', 'pass', 'loot', 'more'];

interface TabBarProps {
  active: TabId;
  onSelect: (t: TabId) => void;
  labels?: Partial<Record<TabId, string>>;
}

export default function TabBar({ active, onSelect, labels }: TabBarProps) {
  return (
    <>
    <nav
      className="flex gap-2 px-4 pt-3 pb-[18px]"
      aria-label="main tabs"
    >
      {ORDER.map((id) => {
        const isActive = id === active;
        return (
          // Ink trim. `pixel-corners` is a clip-path, so a CSS border would be
          // sliced off at every step — the app's idiom for outlining one of
          // these shapes is an ink-filled wrapper with the same clip and a few
          // px of padding (see SnakeCanvas). The wrapper carries flex-1 so the
          // four tabs still share the row evenly.
          <div
            key={id}
            className="flex-1 pixel-corners bg-primary p-[2px]"
          >
            <button
              onClick={() => onSelect(id)}
              className={`w-full min-h-[40px] py-3 pixel-corners font-heading text-[8px] transition-colors ${
                isActive
                  ? 'bg-coral text-white'
                  : 'bg-tab-inactive text-body-grey cursor-pointer'
              }`}
            >
              {(labels?.[id]) ?? DEFAULT_LABELS[id]}
            </button>
          </div>
        );
      })}
    </nav>
    {/* Coral base cap — mirrors the header's rounded shoulders at the bottom
        of the shell. Safari paints its bottom chrome coral (it samples our
        background) but the chrome itself can't be shaped, so the page
        supplies the rounded transition from inside; the cap also swallows
        the bottom safe-area inset (PWA home-indicator zone) so it sits
        flush on the screen edge. Moved here from the nav's padding. */}
    <div aria-hidden className="shrink-0 bg-coral rounded-t-[24px] pt-[14px] pb-[env(safe-area-inset-bottom)]" />
    </>
  );
}
